import { useId } from 'react';
import { Link2, Hammer, BrainCircuit, Target } from 'lucide-react';

/* ─── Data ─────────────────────────────────────────────── */
const CARDS = [
  {
    icon: Link2,
    title: 'We connect the dots.',
    description:
      'Your website, advertising, data and creative shouldn\'t be working independently.',
  },
  {
    icon: Hammer,
    title: 'We build, not just advise.',
    description:
      'Strategy is only useful when it becomes something real.',
  },
  {
    icon: BrainCircuit,
    title: 'We use AI where it creates an advantage.',
    description:
      'Not because putting "AI-powered" on a website sounds impressive.',
  },
  {
    icon: Target,
    title: 'We care about the outcome.',
    description:
      'Traffic, conversions, efficiency, speed, insight — whatever actually matters to the project.',
  },
];

/* ─── SVG Grid Pattern (ported from reference) ─────────── */
function GridPattern({ width, height, x, y, squares, ...props }) {
  const patternId = useId();
  return (
    <svg aria-hidden="true" {...props}>
      <defs>
        <pattern
          id={patternId}
          width={width}
          height={height}
          patternUnits="userSpaceOnUse"
          x={x}
          y={y}
        >
          <path d={`M.5 ${height}V.5H${width}`} fill="none" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" strokeWidth={0} fill={`url(#${patternId})`} />
      {squares && (
        <svg x={x} y={y} style={{ overflow: 'visible' }}>
          {squares.map(([sx, sy]) => (
            <rect
              strokeWidth="0"
              key={`${sx}-${sy}`}
              width={width + 1}
              height={height + 1}
              x={sx * width}
              y={sy * height}
            />
          ))}
        </svg>
      )}
    </svg>
  );
}

/* ─── Grid Overlay (ported from reference) ──────────────── */
function Grid({ size = 20 }) {
  const squares = [
    [7, 2], [8, 4], [9, 1], [10, 3], [11, 5],
  ];
  return (
    <div style={{
      pointerEvents: 'none',
      position: 'absolute',
      left: '50%',
      top: 0,
      marginLeft: '-80px',
      marginTop: '-8px',
      height: '100%',
      width: '100%',
      WebkitMaskImage: 'linear-gradient(white, transparent)',
      maskImage: 'linear-gradient(white, transparent)',
    }}>
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(farthest-side at top, rgba(255,255,255,0.04), transparent)',
        WebkitMaskImage: 'radial-gradient(farthest-side at top, white, transparent)',
        maskImage: 'radial-gradient(farthest-side at top, white, transparent)',
        opacity: 1,
      }}>
        <GridPattern
          width={size}
          height={size}
          x="-12"
          y="4"
          squares={squares}
          style={{
            position: 'absolute',
            inset: 0,
            height: '100%',
            width: '100%',
            mixBlendMode: 'overlay',
            fill: 'rgba(255,255,255,0.07)',
            stroke: 'rgba(255,255,255,0.07)',
          }}
        />
      </div>
    </div>
  );
}

/* ─── Single Card ───────────────────────────────────────── */
function Card({ card, index }) {
  const Icon = card.icon;
  return (
    <div className="wa-card" style={{ animationDelay: `${index * 0.1}s` }}>
      {/* gradient grid bg overlay */}
      <Grid size={20} />

      {/* number */}
      <span className="wa-card-num">0{index + 1}</span>

      {/* icon */}
      <div className="wa-card-icon-wrap">
        <Icon size={22} strokeWidth={1.6} color="#ec2b28" />
      </div>

      {/* text */}
      <p className="wa-card-title">{card.title}</p>
      <p className="wa-card-desc">{card.description}</p>
    </div>
  );
}

/* ─── Section ───────────────────────────────────────────── */
export default function WhyAurelix() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap');

        /* ── Section wrapper ── */
        .wa-section {
          background: #080808;
          font-family: 'Bricolage Grotesque', sans-serif;
          padding: clamp(56px, 9vh, 112px) clamp(20px, 6vw, 96px);
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        /* ── Header ── */
        .wa-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 14px;
          margin-bottom: clamp(40px, 6vh, 72px);
        }
        .wa-eyebrow {
          font-family: 'Bricolage Grotesque', sans-serif;
          display: inline-block;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #ec2b28;
          margin-bottom: 16px;
          text-align: center;
        }
        .wa-heading {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(2.4rem, 5.5vw, 4.5rem);
          font-weight: 800;
          letter-spacing: -0.035em;
          color: #fff;
          line-height: 1.08;
          margin: 0;
          text-align: center;
          text-transform: none;
        }
        .wa-heading em {
          font-style: normal;
          color: #ec2b28;
        }

        /* ── Grid ── */
        .wa-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        /* ── Card ── */
        .wa-card {
          position: relative;
          background: linear-gradient(160deg, #1a1a1a 0%, #0d0d0d 100%);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px;
          padding: clamp(24px, 3vw, 36px) clamp(20px, 2.5vw, 30px);
          overflow: hidden;
          transition: border-color 0.25s, transform 0.25s, box-shadow 0.25s;
          cursor: default;
        }
        .wa-card:hover {
          border-color: rgba(236,43,40,0.35);
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(0,0,0,0.4), 0 0 0 1px rgba(236,43,40,0.15);
        }

        /* number badge */
        .wa-card-num {
          position: absolute;
          top: 18px;
          right: 20px;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.16em;
          color: rgba(255,255,255,0.4);
          z-index: 2;
        }

        /* icon */
        .wa-card-icon-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(236,43,40,0.15);
          border: 1px solid rgba(236,43,40,0.3);
          margin-bottom: 20px;
          position: relative;
          z-index: 2;
        }

        /* title */
        .wa-card-title {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(1.1rem, 1.5vw, 1.3rem);
          font-weight: 700;
          color: #ffffff;
          line-height: 1.25;
          margin: 0 0 12px;
          position: relative;
          z-index: 2;
          letter-spacing: -0.02em;
        }
        .wa-card:hover .wa-card-title {
          color: #ffffff;
        }

        /* desc */
        .wa-card-desc {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 0.85rem;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.55);
          line-height: 1.65;
          margin: 0;
          position: relative;
          z-index: 2;
        }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .wa-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 560px) {
          .wa-grid {
            grid-template-columns: 1fr;
          }
          .wa-section {
            padding-left: 16px;
            padding-right: 16px;
          }
        }
      `}</style>

      <section className="wa-section">

        <div className="wa-header">
          <span className="wa-eyebrow">Why Aurelix</span>
          <h2 className="wa-heading">
            Different disciplines.<br />
            <em>One direction.</em>
          </h2>
        </div>

        <div className="wa-grid">
          {CARDS.map((card, i) => (
            <Card key={i} card={card} index={i} />
          ))}
        </div>

      </section>
    </>
  );
}
